/**
 * persistence/firestore/firestoreSync.js
 *
 * All Firestore write operations.  Never called in the hot Redux path —
 * always via the firestoreQueue table in IndexedDB.
 *
 * Firestore data model:
 *
 *   /analystSessions/{analystId}_{matchId}/
 *     currentString: string
 *     eventCount:    number
 *     updatedAt:     Timestamp
 *     matchId:       string
 *     analystId:     string
 *
 *   /analystSessions/{analystId}_{matchId}/versions/{versionId}/
 *     string:        string
 *     eventCount:    number
 *     triggerType:   string
 *     createdAt:     Timestamp
 *     diffSummary:   { added: number, removed: number }
 *
 *   /analystSessions/{analystId}_{matchId}/diffLog/{diffId}/
 *     timestamp:      Timestamp
 *     addedEvents:    string[]
 *     removedEvents:  string[]
 *     prevEventCount: number
 *     newEventCount:  number
 *
 * Write triggers (all via the IndexedDB queue, never direct):
 *   'session-backup' — live currentString backup (on api-success)
 *   'checkpoint'     — version snapshot (on CheckpointManager trigger)
 *   'diff'           — diff batch (periodic flush of diffLog)
 *
 * Free-tier write budget estimate for a 90-min match:
 *   session-backup:  ~45  (one per 2-min API sync)
 *   checkpoints:     ~18  (one per 5 min)
 *   diff batches:    ~18  (batched, one per 5 min)
 *   Total:           ~81 writes/match  ← well within free tier limits
 */

import { firestoreQueueTable, versionsTable } from "../db/database";
import { getFirestoreClient } from "./firestoreClient";
import {
  FIRESTORE_SESSIONS_COLLECTION,
  FIRESTORE_BACKUP_PERIODIC_MS,
  FIRESTORE_VERSIONS_CLOUD_MAX,
} from "../config";
import { getClientInfo } from "../clientInfo";

// ─── Queue management ─────────────────────────────────────────────────────────

/**
 * Adds a write request to the Firestore queue in IndexedDB.
 * The queue is flushed periodically by the Firestore flush timer (started in
 * startFirestoreSync) and immediately on beforeunload.
 *
 * @param {object} params
 * @param {string}  params.matchId
 * @param {string}  params.analystId
 * @param {string}  params.type      'session-backup' | 'checkpoint' | 'diff'
 * @param {object}  params.payload   Data to write to Firestore
 */
export async function queueFirestoreWrite({ matchId, analystId, type, payload }) {
  try {
    // For 'session-backup' type: upsert (only one pending backup per session)
    if (type === "session-backup") {
      const existing = await firestoreQueueTable()
        .where("[matchId+analystId]")
        .equals([matchId, analystId])
        .filter((r) => r.type === "session-backup" && r.status === "pending")
        .first();

      if (existing) {
        await firestoreQueueTable().update(existing.id, {
          payload,
          queuedAt: Date.now(),
        });
        return;
      }
    }

    await firestoreQueueTable().add({
      matchId,
      analystId,
      type,
      payload,
      queuedAt:  Date.now(),
      attempts:  0,
      status:    "pending",
      lastError: null,
    });
  } catch (error) {
    console.warn("[firestoreSync] queueFirestoreWrite failed:", error.message);
  }
}

// ─── Sync loop ────────────────────────────────────────────────────────────────

let _backupTimer = null;

/**
 * Starts the periodic Firestore flush timer.
 * Called from useSessionPersistence on InputBoard mount.
 */
export function startFirestoreSync() {
  if (_backupTimer) return;
  _backupTimer = setInterval(() => {
    flushFirestoreQueue();
  }, FIRESTORE_BACKUP_PERIODIC_MS);
}

/**
 * Stops the periodic Firestore flush timer.
 * Called from useSessionPersistence on InputBoard unmount.
 */
export function stopFirestoreSync() {
  if (_backupTimer) {
    clearInterval(_backupTimer);
    _backupTimer = null;
  }
}

/**
 * Flushes all pending Firestore queue items.
 * Called periodically and explicitly on beforeunload.
 *
 * @returns {Promise<void>}
 */
export async function flushFirestoreQueue() {
  const db = await getFirestoreClient();
  if (!db) {
    console.warn("[firestoreSync] Firestore client not available — skipping flush.");
    return;
  }

  try {
    const pending = await firestoreQueueTable()
      .where("status")
      .equals("pending")
      .toArray();

    if (pending.length === 0) return;

    console.log(`[firestoreSync] Flushing ${pending.length} pending item(s)…`);
    for (const item of pending) {
      await _processFirestoreItem(item, db);
    }
  } catch (error) {
    console.warn("[firestoreSync] flushFirestoreQueue error:", error.message);
  }
}

// ─── Private: write handlers ──────────────────────────────────────────────────

async function _processFirestoreItem(item, firestoreDb) {
  try {
    await firestoreQueueTable().update(item.id, { status: "processing" });
    console.log(`[firestoreSync] Writing type="${item.type}" for match=${item.matchId}…`);

    switch (item.type) {
      case "session-backup":
        await _writeSessionBackup(item, firestoreDb);
        break;
      case "checkpoint":
        await _writeCheckpoint(item, firestoreDb);
        break;
      case "diff":
        await _writeDiffBatch(item, firestoreDb);
        break;
      default:
        console.warn("[firestoreSync] Unknown queue item type:", item.type);
    }

    await firestoreQueueTable().update(item.id, { status: "done" });
    console.log(`[firestoreSync] ✅ type="${item.type}" written successfully.`);
  } catch (error) {
    const attempts = (item.attempts || 0) + 1;
    await firestoreQueueTable().update(item.id, {
      status:    attempts >= 3 ? "failed" : "pending",
      attempts,
      lastError: error.message,
    });
    console.error(
      `[firestoreSync] ❌ Write FAILED (attempt ${attempts}) type="${item.type}":`,
      error.message
    );
  }
}

/**
 * Writes/updates the live session backup document.
 * Document ID: `{analystId}_{matchId}`
 */
async function _writeSessionBackup(item, firestoreDb) {
  const { doc, setDoc, serverTimestamp } = await import("firebase/firestore");

  const docId  = _sessionDocId(item.matchId, item.analystId);
  const docRef = doc(firestoreDb, FIRESTORE_SESSIONS_COLLECTION, docId);

  await setDoc(docRef, {
    matchId:       item.matchId,
    analystId:     item.analystId,
    currentString: item.payload.currentString,
    eventCount:    item.payload.eventCount ?? 0,
    client:        getClientInfo(),
    updatedAt:     serverTimestamp(),
  }, { merge: true });
}

/**
 * Writes a checkpoint snapshot to the versions subcollection, then prunes
 * the oldest entries so the collection never exceeds FIRESTORE_VERSIONS_CLOUD_MAX.
 */
async function _writeCheckpoint(item, firestoreDb) {
  const {
    collection, addDoc, getDocs, deleteDoc, orderBy, query, serverTimestamp,
  } = await import("firebase/firestore");

  const docId           = _sessionDocId(item.matchId, item.analystId);
  const versionsCollRef = collection(
    firestoreDb,
    FIRESTORE_SESSIONS_COLLECTION,
    docId,
    "versions"
  );

  // Write new checkpoint
  await addDoc(versionsCollRef, {
    string:        item.payload.string,
    eventCount:    item.payload.eventCount,
    triggerType:   item.payload.triggerType,
    createdAt:     serverTimestamp(),
    versionNumber: item.payload.versionNumber ?? null, // stable cross-store identifier
    diffSummary:   item.payload.diffSummary ?? { added: 0, removed: 0 },
    client:        getClientInfo(),
  });

  // Flip synced:true on the originating IDB version record so the history
  // screen can show whether the latest local checkpoint is already in the cloud.
  const idbVersionId = item.payload.idbVersionId;
  if (idbVersionId != null) {
    try {
      await versionsTable().update(idbVersionId, { synced: true });
    } catch {
      // Non-fatal — local DB state is best-effort.
    }
  }

  // Prune oldest checkpoints so the collection stays within the cloud cap.
  // Runs as a best-effort cleanup — failure does not affect the write result.
  try {
    const snapshot = await getDocs(
      query(versionsCollRef, orderBy("createdAt", "asc"))
    );
    const excess = snapshot.docs.length - FIRESTORE_VERSIONS_CLOUD_MAX;
    if (excess > 0) {
      await Promise.all(
        snapshot.docs.slice(0, excess).map((d) => deleteDoc(d.ref))
      );
      console.log(
        `[firestoreSync] Pruned ${excess} old checkpoint(s) — kept ${FIRESTORE_VERSIONS_CLOUD_MAX}.`
      );
    }
  } catch (pruneErr) {
    // Non-fatal — the new checkpoint was already written successfully.
    console.warn("[firestoreSync] Checkpoint prune failed:", pruneErr.message);
  }
}

/**
 * Writes a batch of diff records to the diffLog subcollection.
 */
async function _writeDiffBatch(item, firestoreDb) {
  const { doc, collection, addDoc, serverTimestamp } = await import("firebase/firestore");

  const docId          = _sessionDocId(item.matchId, item.analystId);
  const diffCollRef    = collection(
    firestoreDb,
    FIRESTORE_SESSIONS_COLLECTION,
    docId,
    "diffLog"
  );

  const diffs = item.payload.diffs ?? [];
  // Batch writes — Firestore allows max 500 ops per batch, but diffs are small
  await Promise.all(
    diffs.map((diff) =>
      addDoc(diffCollRef, {
        timestamp:      serverTimestamp(),
        addedEvents:    diff.addedEvents,
        removedEvents:  diff.removedEvents,
        prevEventCount: diff.prevEventCount,
        newEventCount:  diff.newEventCount,
      })
    )
  );
}

// ─── Full sync ────────────────────────────────────────────────────────────────

/**
 * Replaces the entire Firestore versions subcollection with the provided IDB
 * versions, then marks each one as synced:true in IndexedDB.
 *
 * Called by the manual "Sync to Cloud" button on the analyst history screen.
 * This makes Firestore an exact mirror of the local IDB state so the version
 * numbers shown in the management portal match what the analyst sees locally.
 *
 * Write cost: ≤ (existing count) deletes + up to FIRESTORE_VERSIONS_CLOUD_MAX
 * writes = ≤ 20 Firestore operations per manual sync.  Acceptable for a
 * user-triggered action.
 *
 * @param {string}   matchId
 * @param {string}   analystId
 * @param {object[]} idbVersions  Newest-first array from useVersionHistory / getVersions.
 *                                Each entry must have { id, string, eventCount,
 *                                createdAt, triggerType }.
 * @returns {Promise<number>}  Count of versions written to Firestore.
 */
export async function fullSyncVersionsToFirestore(matchId, analystId, idbVersions) {
  const db = await getFirestoreClient();
  if (!db) throw new Error("Firestore client not available — check Firebase config.");

  const {
    collection, getDocs, deleteDoc, addDoc, query, orderBy, Timestamp,
  } = await import("firebase/firestore");

  const docId          = _sessionDocId(matchId, analystId);
  const versionsCollRef = collection(db, FIRESTORE_SESSIONS_COLLECTION, docId, "versions");

  // 1. Delete every existing version document in Firestore for this session.
  //    This is what makes the version counts agree after a sync.
  const existing = await getDocs(query(versionsCollRef, orderBy("createdAt", "asc")));
  if (existing.docs.length > 0) {
    await Promise.all(existing.docs.map((d) => deleteDoc(d.ref)));
  }

  // 2. Write the latest N versions from IDB (newest-first → reverse to oldest-first
  //    so Firestore orderBy("createdAt","asc") returns them in the right order).
  //    Cap at FIRESTORE_VERSIONS_CLOUD_MAX (20) to stay within budget.
  const toWrite = idbVersions.slice(0, FIRESTORE_VERSIONS_CLOUD_MAX); // newest-first slice
  if (toWrite.length === 0) return 0;

  await Promise.all(
    toWrite.map((v) =>
      addDoc(versionsCollRef, {
        string:        v.string,
        eventCount:    v.eventCount,
        triggerType:   v.triggerType,
        // Preserve the original IDB timestamp so management portal shows the
        // correct checkpoint time rather than the sync time.
        createdAt:     Timestamp.fromMillis(v.createdAt),
        versionNumber: v.versionNumber ?? null, // stable cross-store identifier
        diffSummary:   { added: 0, removed: 0 },
      })
    )
  );

  // 3. Flip synced:true on every IDB record that was written.
  //    Non-fatal if any individual update fails.
  await Promise.allSettled(
    toWrite
      .filter((v) => v.id != null)
      .map((v) => versionsTable().update(v.id, { synced: true }))
  );

  return toWrite.length;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function _sessionDocId(matchId, analystId) {
  return `${analystId}_${matchId}`;
}
